"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocale } from "@/components/locale-provider";
import { ExchangePicker, type ExchangeTarget } from "@/components/exchange-picker";
import type { RemovalReason } from "@/lib/removal-reason";

type OpenOptions = {
  entityType: "ITEM" | "BOOK";
  entityId: string;
  username: string;
};

type Result = { action: "cancel" } | { action: "skip" } | { action: "remove"; reason: RemovalReason };

type DialogState = OpenOptions & { resolve: (value: Result) => void };

type ReasonKind = "MISSING" | "TRASH" | "SOLD" | "EXCHANGE" | "OTHERS";

const RemovalReasonDialogContext = createContext<((options: OpenOptions) => Promise<Result>) | null>(null);

export function RemovalReasonDialogProvider({ children }: { children: React.ReactNode }) {
  const { t } = useLocale();
  const [state, setState] = useState<DialogState | null>(null);
  const [reasonKind, setReasonKind] = useState<ReasonKind | null>(null);
  const [soldPrice, setSoldPrice] = useState("");
  const [othersNote, setOthersNote] = useState("");
  const [exchangeTarget, setExchangeTarget] = useState<ExchangeTarget | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const open = state !== null;

  const openDialog = (options: OpenOptions) =>
    new Promise<Result>((resolve) => {
      setReasonKind(null);
      setSoldPrice("");
      setOthersNote("");
      setExchangeTarget(null);
      setState({ ...options, resolve });
    });

  const close = (value: Result) => {
    state?.resolve(value);
    setState(null);
  };

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close({ action: "cancel" });
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buildReason = (): RemovalReason | null => {
    if (reasonKind === "MISSING") return { reason: "MISSING" };
    if (reasonKind === "TRASH") return { reason: "TRASH" };
    if (reasonKind === "SOLD") {
      const price = soldPrice.trim() ? Number(soldPrice) : undefined;
      return { reason: "SOLD", ...(price != null ? { price } : {}) };
    }
    if (reasonKind === "OTHERS") {
      const note = othersNote.trim();
      return { reason: "OTHERS", ...(note ? { note } : {}) };
    }
    if (reasonKind === "EXCHANGE" && exchangeTarget) {
      return { reason: "EXCHANGE", exchangedForType: exchangeTarget.type, exchangedForId: exchangeTarget.id };
    }
    return null;
  };

  const removeDisabled = reasonKind === null || (reasonKind === "EXCHANGE" && !exchangeTarget);

  const handleRemove = () => {
    const reason = buildReason();
    if (!reason) return;
    close({ action: "remove", reason });
  };

  const pills: { kind: ReasonKind; label: string }[] = [
    { kind: "MISSING", label: t.removalReason.missing },
    { kind: "TRASH", label: t.removalReason.trash },
    { kind: "SOLD", label: t.removalReason.sold },
    { kind: "EXCHANGE", label: t.removalReason.exchange },
    { kind: "OTHERS", label: t.removalReason.others },
  ];

  return (
    <RemovalReasonDialogContext.Provider value={openDialog}>
      {children}
      {state && (
        <div
          className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 px-3 pt-3 pb-[max(0.75rem,var(--safe-area-inset-bottom,env(safe-area-inset-bottom)))] backdrop-blur-sm md:items-center md:p-3"
          onClick={() => close({ action: "cancel" })}
        >
          <div
            className="glass-floating glass-specular w-full max-w-sm rounded-ios-lg p-5"
            role="dialog"
            aria-modal="true"
            aria-labelledby="removal-reason-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <p id="removal-reason-dialog-title" className="mb-3 text-[16px] font-semibold text-ios-label">
              {t.removalReason.title}
            </p>

            <div className="flex flex-wrap gap-1.5">
              {pills.map((pill) => (
                <button
                  key={pill.kind}
                  type="button"
                  onClick={() => setReasonKind(pill.kind)}
                  className={`press-shrink rounded-full px-3 py-1.5 text-[13px] font-medium ${
                    reasonKind === pill.kind ? "bg-ios-accent text-white" : "bg-ios-fill text-ios-label"
                  }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>

            {reasonKind === "SOLD" && (
              <div className="mt-3">
                <label className="mb-1 block text-[11px] text-ios-label-secondary">
                  {t.removalReason.soldPriceLabel}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={soldPrice}
                  onChange={(e) => setSoldPrice(e.target.value)}
                  className="w-full rounded-ios-sm bg-ios-fill px-3 py-2 text-[16px] text-ios-label outline-none"
                />
              </div>
            )}

            {reasonKind === "OTHERS" && (
              <div className="mt-3">
                <textarea
                  value={othersNote}
                  onChange={(e) => setOthersNote(e.target.value.slice(0, 500))}
                  placeholder={t.removalReason.othersPlaceholder}
                  rows={3}
                  className="w-full resize-none rounded-ios-sm bg-ios-fill px-3 py-2 text-[14px] text-ios-label outline-none placeholder:text-ios-label-tertiary"
                />
              </div>
            )}

            {reasonKind === "EXCHANGE" && state && (
              <div>
                {exchangeTarget ? (
                  <div className="mt-3 flex items-center justify-between gap-2 rounded-ios-sm bg-ios-fill px-3 py-2">
                    <span className="min-w-0 truncate text-[13px] text-ios-label">
                      {t.removalReason.exchangeSelected(exchangeTarget.title)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setExchangeTarget(null)}
                      className="shrink-0 text-[12px] font-medium text-ios-accent"
                    >
                      {t.removalReason.exchangeChange}
                    </button>
                  </div>
                ) : (
                  <ExchangePicker
                    username={state.username}
                    excludeType={state.entityType}
                    excludeId={state.entityId}
                    onSelect={setExchangeTarget}
                    onNavigateAway={() => close({ action: "cancel" })}
                  />
                )}
              </div>
            )}

            <div className="mt-5 flex gap-2">
              <button
                ref={cancelRef}
                type="button"
                onClick={() => close({ action: "skip" })}
                className="press-shrink flex-1 rounded-full bg-ios-fill px-4 py-2.5 text-[14px] font-semibold text-ios-label"
              >
                {t.removalReason.skip}
              </button>
              <button
                type="button"
                disabled={removeDisabled}
                onClick={handleRemove}
                className="glass-specular press-shrink flex-1 rounded-full bg-ios-destructive px-4 py-2.5 text-[14px] font-semibold text-white disabled:opacity-50"
              >
                {t.itemActions.remove}
              </button>
            </div>
          </div>
        </div>
      )}
    </RemovalReasonDialogContext.Provider>
  );
}

export function useRemovalReasonDialog() {
  const ctx = useContext(RemovalReasonDialogContext);
  if (!ctx) throw new Error("useRemovalReasonDialog must be used within a RemovalReasonDialogProvider");
  return ctx;
}
